import { getCurrencySymbol } from './currency';
import {
  RENTAL_PACKAGE_GRACE_MAX,
  RENTAL_PACKAGE_GRACE_MAX_DIGITS,
  RENTAL_PACKAGE_GRACE_MIN,
  RENTAL_PACKAGE_EXTRA_KM_MAX,
  RENTAL_PACKAGE_EXTRA_KM_MAX_DIGITS,
  RENTAL_PACKAGE_HOURS_MAX,
  RENTAL_PACKAGE_HOURS_MIN,
  RENTAL_PACKAGE_KM_MAX,
  RENTAL_PACKAGE_KM_MIN,
  RENTAL_PACKAGE_PRICE_MAX,
  RENTAL_PACKAGE_PRICE_MAX_DIGITS,
  RENTAL_PACKAGE_PRICE_MIN,
} from '../constants/transportPartnerFlow';

export interface RentalPackageRow {
  _id: string;
  code: string;
  name: string;
  isActive?: boolean;
  metadata?: Record<string, unknown>;
}

export interface RentalVehiclePrice {
  vehicleTypeCode: string;
  vehicleTypeName: string;
  price: number;
  graceTimeMin: number;
  extraKmPrice: number;
  rowId?: string;
  rowCode?: string;
}

export interface RentalPackageGroup {
  groupKey: string;
  packageGroupCode: string;
  packageHr: number;
  packageKm: number;
  zoneCode: string;
  zoneName?: string;
  serviceSlug: string;
  vehiclePrices: RentalVehiclePrice[];
  isActive: boolean;
}

export function buildPackageGroupCode(zoneCode: string, packageHr: number, packageKm: number) {
  return `${zoneCode}_${packageHr}H${packageKm}KM`.replace(/[^A-Za-z0-9_]/g, '_');
}

/** Stable unique key for a rental package group row (zone + service + hours + km). */
export function buildRentalPackageGroupKey(input: {
  zoneCode: string;
  serviceSlug?: string;
  packageHr: number;
  packageKm: number;
  packageGroupCode?: string;
}): string {
  const serviceSlug = input.serviceSlug || 'car-rental';
  const packageGroupCode = input.packageGroupCode
    || buildPackageGroupCode(input.zoneCode, input.packageHr, input.packageKm);
  return `${input.zoneCode}::${serviceSlug}::${input.packageHr}::${input.packageKm}::${packageGroupCode}`;
}

export function isSameRentalPackageGroup(
  a: Pick<RentalPackageGroup, 'groupKey' | 'zoneCode' | 'serviceSlug' | 'packageHr' | 'packageKm' | 'packageGroupCode'>,
  b: Pick<RentalPackageGroup, 'groupKey' | 'zoneCode' | 'serviceSlug' | 'packageHr' | 'packageKm' | 'packageGroupCode'>,
): boolean {
  return buildRentalPackageGroupKey(a) === buildRentalPackageGroupKey(b);
}

export function patchRentalPackageGroups(
  groups: RentalPackageGroup[],
  target: RentalPackageGroup,
  patch: Partial<RentalPackageGroup>,
): RentalPackageGroup[] {
  return groups.map((g) => (isSameRentalPackageGroup(g, target) ? { ...g, ...patch } : g));
}

export function validateRentalPackageHours(
  value: number | string | null | undefined,
): string | undefined {
  if (value === '' || value == null || !Number.isFinite(Number(value))) {
    return 'Enter valid package hours.';
  }
  const n = Number(value);
  if (n < 0) return 'Package hours cannot be negative.';
  if (!Number.isInteger(n)) return 'Package hours must be a whole number.';
  if (n <= 0) return 'Package hours must be greater than 0.';
  if (n > RENTAL_PACKAGE_HOURS_MAX) {
    return `Package hours cannot exceed ${RENTAL_PACKAGE_HOURS_MAX.toLocaleString('en-IN')} (1 month).`;
  }
  return undefined;
}

export function validateRentalPackageKm(
  value: number | string | null | undefined,
): string | undefined {
  if (value === '' || value == null || !Number.isFinite(Number(value))) {
    return 'Enter valid package KM.';
  }
  const n = Number(value);
  if (n < 0) return 'Package KM cannot be negative.';
  if (!Number.isInteger(n)) return 'Package KM must be a whole number.';
  if (n <= 0) return 'Package KM must be greater than 0.';
  if (n > RENTAL_PACKAGE_KM_MAX) {
    return `Package KM cannot exceed ${RENTAL_PACKAGE_KM_MAX.toLocaleString('en-IN')}.`;
  }
  return undefined;
}

export function normalizeRentalPackageHours(value: number, fallback = RENTAL_PACKAGE_HOURS_MIN): number {
  const n = Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < RENTAL_PACKAGE_HOURS_MIN) return fallback;
  return Math.min(n, RENTAL_PACKAGE_HOURS_MAX);
}

export function normalizeRentalPackageKm(value: number, fallback = RENTAL_PACKAGE_KM_MIN): number {
  const n = Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < RENTAL_PACKAGE_KM_MIN) return fallback;
  return Math.min(n, RENTAL_PACKAGE_KM_MAX);
}

/** @deprecated Use normalizeRentalPackageHours / normalizeRentalPackageKm */
export function normalizeRentalPackageQuantity(value: number, fallback = 1): number {
  return normalizeRentalPackageHours(value, fallback);
}

export function validateRentalPackageBundle(
  packageHr: number,
  packageKm: number,
): string | undefined {
  const hrError = validateRentalPackageHours(packageHr);
  const kmError = validateRentalPackageKm(packageKm);
  if (hrError && kmError) return 'Package hours and KM cannot both be zero.';
  if (Number(packageHr) <= 0 && Number(packageKm) <= 0) {
    return 'Package hours and KM cannot both be zero.';
  }
  return undefined;
}

export function validateRentalVehiclePrice(
  value: number | string | null | undefined,
): string | undefined {
  if (value === '' || value == null || !Number.isFinite(Number(value))) {
    return 'Price is required for every vehicle.';
  }
  const n = Number(value);
  if (n < 0) return 'Price cannot be negative.';
  if (!Number.isInteger(n)) return 'Price must be a whole number.';
  if (n < RENTAL_PACKAGE_PRICE_MIN) return `Price must be at least ${getCurrencySymbol()}${RENTAL_PACKAGE_PRICE_MIN}.`;
  if (n > RENTAL_PACKAGE_PRICE_MAX) {
    return `Price cannot exceed ${getCurrencySymbol()}${RENTAL_PACKAGE_PRICE_MAX.toLocaleString('en-IN')} (${RENTAL_PACKAGE_PRICE_MAX_DIGITS} digits).`;
  }
  return undefined;
}

export function validateRentalGraceMinutes(
  value: number | string | null | undefined,
): string | undefined {
  if (value === '' || value == null || !Number.isFinite(Number(value))) {
    return 'Enter valid grace minutes.';
  }
  const n = Number(value);
  if (n < 0) return 'Grace minutes cannot be negative.';
  if (!Number.isInteger(n)) return 'Grace minutes must be a whole number.';
  if (n > RENTAL_PACKAGE_GRACE_MAX) {
    return `Grace minutes cannot exceed ${RENTAL_PACKAGE_GRACE_MAX.toLocaleString('en-IN')} (${RENTAL_PACKAGE_GRACE_MAX_DIGITS} digits).`;
  }
  return undefined;
}

export function validateRentalExtraKmPrice(
  value: number | string | null | undefined,
): string | undefined {
  if (value === '' || value == null) return undefined;
  if (!Number.isFinite(Number(value))) return 'Enter valid extra KM price.';
  const n = Number(value);
  if (n < 0) return 'Extra KM price cannot be negative.';
  if (!Number.isInteger(n)) return 'Extra KM price must be a whole number.';
  if (n > RENTAL_PACKAGE_EXTRA_KM_MAX) {
    return `Extra KM price cannot exceed ${getCurrencySymbol()}${RENTAL_PACKAGE_EXTRA_KM_MAX.toLocaleString('en-IN')} (${RENTAL_PACKAGE_EXTRA_KM_MAX_DIGITS} digits).`;
  }
  return undefined;
}

export interface RentalPackageDuplicateCheck {
  zoneCode: string;
  packageHr: number;
  packageKm: number;
  serviceSlug: string;
  excludeGroupCode?: string;
  /** When true, treat bike/car/rental slugs as the same package slot for a zone. */
  ignoreServiceSlug?: boolean;
}

export function findDuplicateRentalPackageGroup(
  groups: RentalPackageGroup[],
  params: RentalPackageDuplicateCheck,
): RentalPackageGroup | undefined {
  const serviceSlug = params.serviceSlug || 'car-rental';
  const hr = Number(params.packageHr);
  const km = Number(params.packageKm);
  return groups.find((group) => {
    if (params.excludeGroupCode && group.packageGroupCode === params.excludeGroupCode) return false;
    if (group.zoneCode !== params.zoneCode) return false;
    if (Number(group.packageHr) !== hr) return false;
    if (Number(group.packageKm) !== km) return false;
    if (params.ignoreServiceSlug) return true;
    const groupService = group.serviceSlug || 'car-rental';
    return groupService === serviceSlug;
  });
}

export function getDuplicateRentalPackageMessage(
  groups: RentalPackageGroup[],
  params: RentalPackageDuplicateCheck,
): string | undefined {
  const match = findDuplicateRentalPackageGroup(groups, params);
  if (!match) return undefined;
  if (params.ignoreServiceSlug) {
    return `A ${match.packageHr}h / ${match.packageKm}km package already exists for this zone. Edit the existing package or choose different hours/KM.`;
  }
  return `A ${match.packageHr}h / ${match.packageKm}km package already exists for this zone and service. Edit the existing package or choose different hours/KM.`;
}

export function isRentalDuplicateApiResponse(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const record = data as Record<string, unknown>;
  return record.isDuplicate === true || record.duplicate === true;
}

export function rentalDuplicateApiMessage(data: unknown, fallback: string): string {
  if (data && typeof data === 'object') {
    const message = (data as { message?: string }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return fallback;
}

export interface RentalZoneHierarchyRow {
  _id: string;
  code: string;
  name: string;
  zoneLevel: string;
  primaryZoneId?: string;
  isActive: boolean;
  packageCount: number;
  activePackageCount: number;
  hasPackages: boolean;
}

export function buildRentalZoneHierarchy(
  rentalZones: RentalZoneHierarchyRow[],
  zoneMeta: Array<{ _id: string; primaryZoneId?: string; zoneLevel?: string }>,
): {
  primaryZones: RentalZoneHierarchyRow[];
  secondariesByPrimaryId: Map<string, RentalZoneHierarchyRow[]>;
} {
  const metaById = new Map(zoneMeta.map((z) => [z._id, z]));
  const enriched = rentalZones.map((z) => {
    const meta = metaById.get(z._id);
    return {
      ...z,
      primaryZoneId: meta?.primaryZoneId ?? z.primaryZoneId,
      zoneLevel: meta?.zoneLevel ?? z.zoneLevel,
    };
  });

  const primaryZones = enriched
    .filter((z) => z.zoneLevel === 'PRIMARY')
    .sort((a, b) => a.name.localeCompare(b.name));

  const secondariesByPrimaryId = new Map<string, RentalZoneHierarchyRow[]>();
  for (const zone of enriched) {
    if (zone.zoneLevel !== 'SECONDARY' || !zone.primaryZoneId) continue;
    const siblings = secondariesByPrimaryId.get(zone.primaryZoneId) || [];
    siblings.push(zone);
    secondariesByPrimaryId.set(zone.primaryZoneId, siblings);
  }
  for (const siblings of secondariesByPrimaryId.values()) {
    siblings.sort((a, b) => a.name.localeCompare(b.name));
  }

  return { primaryZones, secondariesByPrimaryId };
}

export function filterPrimaryZonesForSearch(
  primaryZones: RentalZoneHierarchyRow[],
  secondariesByPrimaryId: Map<string, RentalZoneHierarchyRow[]>,
  search: string,
): RentalZoneHierarchyRow[] {
  const term = search.trim().toLowerCase();
  if (!term) return primaryZones;
  return primaryZones.filter((primary) => {
    const matchesPrimary = primary.name.toLowerCase().includes(term)
      || primary.code.toLowerCase().includes(term);
    const matchesChild = (secondariesByPrimaryId.get(primary._id) || []).some(
      (child) => child.name.toLowerCase().includes(term) || child.code.toLowerCase().includes(term),
    );
    return matchesPrimary || matchesChild;
  });
}

export function groupRentalPackages(
  rows: RentalPackageRow[],
  zoneNameMap: Map<string, string>,
  vehicleNameMap: Map<string, string>,
): RentalPackageGroup[] {
  const map = new Map<string, RentalPackageGroup>();

  for (const row of rows) {
    const meta = row.metadata || {};
    const zoneCode = String(meta.zoneCode || '');
    const packageHr = Number(meta.packageHr || 0);
    const packageKm = Number(meta.packageKm || 0);
    const serviceSlug = String(meta.serviceSlug || '');
    const packageGroupCode = String(meta.packageGroupCode || buildPackageGroupCode(zoneCode, packageHr, packageKm));
    const groupKey = buildRentalPackageGroupKey({
      zoneCode,
      serviceSlug,
      packageHr,
      packageKm,
      packageGroupCode,
    });

    let group = map.get(groupKey);
    if (!group) {
      group = {
        groupKey,
        packageGroupCode,
        packageHr,
        packageKm,
        zoneCode,
        zoneName: zoneNameMap.get(zoneCode) || zoneCode,
        serviceSlug,
        vehiclePrices: [],
        isActive: row.isActive !== false,
      };
      map.set(groupKey, group);
    }

    const vehicleTypeCode = String(meta.vehicleTypeCode || meta.vehicleType || '');
    group.vehiclePrices.push({
      vehicleTypeCode,
      vehicleTypeName: vehicleNameMap.get(vehicleTypeCode) || vehicleTypeCode,
      price: Number(meta.price || 0),
      graceTimeMin: Number(meta.graceTimeMin || 0),
      extraKmPrice: Number(meta.extraKmPrice || 0),
      rowId: row._id,
      rowCode: row.code,
    });
    if (row.isActive === false) group.isActive = false;
  }

  return Array.from(map.values()).sort((a, b) => {
    if (a.zoneCode !== b.zoneCode) return a.zoneCode.localeCompare(b.zoneCode);
    if (a.packageHr !== b.packageHr) return a.packageHr - b.packageHr;
    return a.packageKm - b.packageKm;
  });
}

export function isRentalVehicleType(serviceTypes?: string) {
  if (!serviceTypes) return false;
  return serviceTypes.split(',').map((s) => s.trim().toUpperCase()).includes('RENTAL');
}

/** Map zone-first rental API groups to panel rows (same shape as groupRentalPackages output). */
export function mapApiRentalGroups(
  groups: Array<{
    packageGroupCode: string;
    packageHr: number;
    packageKm: number;
    zoneCode: string;
    serviceSlug: string;
    isActive: boolean;
    vehiclePrices: Array<{
      vehicleTypeCode: string;
      price: number;
      graceTimeMin?: number;
      extraKmPrice?: number;
      rowId?: string;
      rowCode?: string;
    }>;
  }>,
  zoneNameMap: Map<string, string>,
  vehicleNameMap: Map<string, string>,
): RentalPackageGroup[] {
  return groups.map((g) => ({
    groupKey: buildRentalPackageGroupKey({
      zoneCode: g.zoneCode,
      serviceSlug: g.serviceSlug,
      packageHr: g.packageHr,
      packageKm: g.packageKm,
      packageGroupCode: g.packageGroupCode,
    }),
    packageGroupCode: g.packageGroupCode,
    packageHr: g.packageHr,
    packageKm: g.packageKm,
    zoneCode: g.zoneCode,
    zoneName: zoneNameMap.get(g.zoneCode) || g.zoneCode,
    serviceSlug: g.serviceSlug,
    isActive: g.isActive !== false,
    vehiclePrices: (g.vehiclePrices || []).map((vp) => ({
      vehicleTypeCode: vp.vehicleTypeCode,
      vehicleTypeName: vehicleNameMap.get(vp.vehicleTypeCode) || vp.vehicleTypeCode,
      price: Number(vp.price || 0),
      graceTimeMin: Number(vp.graceTimeMin || 0),
      extraKmPrice: Number(vp.extraKmPrice || 0),
      rowId: vp.rowId,
      rowCode: vp.rowCode,
    })),
  })).sort((a, b) => {
    if (a.zoneCode !== b.zoneCode) return a.zoneCode.localeCompare(b.zoneCode);
    if (a.packageHr !== b.packageHr) return a.packageHr - b.packageHr;
    return a.packageKm - b.packageKm;
  });
}
